// SMART DEMO — Ready Example Mode (FAZ 6A)
// Hazır örnek preset'leri + canlı referans config'i.
//
// Preset listesi SECTOR_CONFIGS üzerinden türetilir (ayrı bir sektör listesi YAZILMAZ).
// Hazır örnek, kendi motorunu YAZMAZ — preset DemoFormData'ya çevrilir ve
// mevcut standard_sector_flow_v1 + DemoSimulator tek motor olarak kullanılır.
//
// NOT (FINAL EXPORT): "Luna Beauty Studio" kurgu ready preset'i aktiftir (`enabled: true`).
// Diğer sektörlerin hazır örnekleri kontrolümüzde bundled sample media bulunmadığı için
// `enabled: false` (kart "Hazırlanıyor") durumundadır. Dış Stable Diffusion / hotlink URL
// üretilmez ve sahte iletişim bilgisi (fake WhatsApp / Instagram / website) yazılmaz.
// Luna medyası kullanıcının kendi serverından servis edilir; download sonrası yalnız
// binary dosyalar public/ klasörlerine kopyalanır, hiçbir kod değiştirilmez.

import type { DemoFormData, ServiceCategory, Campaign } from "./demoTypes";
import { defaultDemoFormData } from "./demoTypes";
import { SECTOR_CONFIGS, type SectorCta } from "./sectorConfigs";
import { LUNA_BEAUTY_MANIFEST } from "./presets/lunaBeautyManifest";
import { NOVA_CLEAN_MANIFEST } from "./presets/novaCleanManifest";
import { LAVIA_EVENT_MANIFEST } from "./presets/laviaEventManifest";
import { PUSULA_AKADEMI_MANIFEST } from "./presets/pusulaAkademiManifest";
import { VISTA_PRIME_MANIFEST } from "./presets/vistaPrimeManifest";
import { MIRA_MUTFAK_MANIFEST } from "./presets/miraMutfakManifest";
import { ARTEVA_HOME_MANIFEST } from "./presets/artevaHomeManifest";
import { VITA_NOVA_NETWORK_MANIFEST } from "./presets/vitaNovaNetworkManifest";
import { MIRA_STYLE_MANIFEST } from "./presets/miraStyleManifest";
import { ANADOLU_SEPETI_MANIFEST } from "./presets/anadoluSepetiManifest";
import { NEXORA_CONSULTING_MANIFEST } from "./presets/nexoraConsultingManifest";
import { MAVI_NOKTA_MANIFEST } from "./presets/maviNoktaManifest";

export interface ReadyExampleContact {
  whatsapp: string;
  instagram: string;
  website: string;
}

export interface ReadyExamplePreset {
  id: string;
  sectorId: string;
  title: string;
  businessName: string;
  logo?: string | null;
  serviceCategories: ServiceCategory[];
  videos: string[];
  campaigns: Campaign[];
  contact: ReadyExampleContact;
  selectedCtas: SectorCta[];
  years?: number;
  strengths?: string[];
  trustNote?: string;
  enabled: boolean;
}

/* Kart başlıkları — sektör id → örnek adı. Sektör listesi SECTOR_CONFIGS'ten gelir,
   bu map yalnızca başlık (wording) sağlar. */
const READY_EXAMPLE_TITLES: Record<string, string> = {
  beauty: "Güzellik Merkezi Örneği",
  cleaning: "Temizlik Firması Örneği",
  event: "Davet Salonu Örneği",
  education: "Eğitim / Kurs Örneği",
  realestate: "Emlak Örneği",
  food: "Yeme & İçme Örneği",
  furniture: "Mobilya / Ev Dekorasyon Örneği",
  network: "Network Marketing Örneği",
  onlinesales: "Online Satış Örneği",
  localproducts: "Yöresel Ürünler Örneği",
  professional: "Profesyonel Hizmet Örneği",
  other: "Genel Hizmet Örneği",
};

/* ---------------- Luna Beauty Studio (kurgu showcase preset) ----------------
   İlk gerçek ready example. Tamamen KURGU demo markadır; gerçek müşteri/firma
   değildir. Medya path'leri LUNA_BEAUTY_MANIFEST'ten, CTA'lar SECTOR_CONFIGS
   beauty defaults'tan (reuse) gelir. Contact bilgisi bilerek boştur.
   `enabled` artık manifest flag'ine bağlı DEĞİLDİR; final export'ta `true`'dur.
   Download sonrası kullanıcı yalnız binary dosyaları kopyalar, kod değiştirmez. */

const BEAUTY_CFG = SECTOR_CONFIGS.find((c) => c.id === "beauty");

const LUNA_BEAUTY_PRESET: ReadyExamplePreset = {
  id: "luna-beauty-v1",
  sectorId: "beauty",
  title: "Güzellik Merkezi Örneği",
  businessName: "Luna Beauty Studio",
  logo: LUNA_BEAUTY_MANIFEST.logo,
  serviceCategories: [
    { id: "laser", name: "Lazer Epilasyon", images: LUNA_BEAUTY_MANIFEST.laser },
    { id: "skincare", name: "Cilt Bakımı", images: LUNA_BEAUTY_MANIFEST.skincare },
    {
      id: "microblading",
      name: "Microblading / Kaş Tasarımı",
      images: LUNA_BEAUTY_MANIFEST.microblading,
    },
  ],
  videos: LUNA_BEAUTY_MANIFEST.videos,
  campaigns: [
    {
      id: "camp-laser",
      name: "Lazer Epilasyon",
      oldPrice: "5.000 TL",
      newPrice: "3.250 TL",
      description: "Seçili bölgelerde tanışma kampanyası.",
    },
    {
      id: "camp-hydrafacial",
      name: "Hydrafacial / Yeni Nesil Cilt Bakımı",
      oldPrice: "2.500 TL",
      newPrice: "1.750 TL",
      description: "Cildinize özel bakım uygulaması.",
    },
    {
      id: "camp-microblading",
      name: "Microblading",
      oldPrice: "3.500 TL",
      newPrice: "1.990 TL",
      description: "Doğal görünüm odaklı kaş tasarımı.",
    },
  ],
  contact: { whatsapp: "", instagram: "", website: "" },
  selectedCtas: BEAUTY_CFG?.defaultCtas ?? [],
  years: 9,
  strengths: ["Uzman Personel", "Premium Hizmet", "Yüksek Müşteri Memnuniyeti"],
  trustNote:
    "Modern uygulama teknikleri, özenli hizmet anlayışı ve müşteri memnuniyetini merkeze alan yaklaşımımızla güzellik ve bakım alanında hizmet veriyoruz.",
  enabled: true,
};

/* ---------------- Nova Clean Premium (kurgu showcase preset) ----------------
   İkinci ready example. Tamamen KURGU demo markadır; gerçek müşteri/firma
   değildir. Medya path'leri NOVA_CLEAN_MANIFEST'ten, CTA'lar SECTOR_CONFIGS
   cleaning defaults'tan (reuse) gelir. Contact bilgisi bilerek boştur.
   `enabled` manifest flag'ine bağlı DEĞİLDİR; final export'ta `true`'dur.
   Download sonrası kullanıcı yalnız binary dosyaları kopyalar, kod değiştirmez. */

const CLEANING_CFG = SECTOR_CONFIGS.find((c) => c.id === "cleaning");

const NOVA_CLEAN_PRESET: ReadyExamplePreset = {
  id: "nova-clean-v1",
  sectorId: "cleaning",
  title: "Temizlik Firması Örneği",
  businessName: "NOVA CLEAN PREMIUM",
  logo: NOVA_CLEAN_MANIFEST.logo,
  serviceCategories: [
    { id: "home-cleaning", name: "Ev Temizliği", images: NOVA_CLEAN_MANIFEST.homeCleaning },
    {
      id: "post-construction",
      name: "İnşaat Sonrası Temizlik",
      images: NOVA_CLEAN_MANIFEST.postConstruction,
    },
    { id: "sofa-cleaning", name: "Koltuk Yıkama", images: NOVA_CLEAN_MANIFEST.sofaCleaning },
  ],
  videos: NOVA_CLEAN_MANIFEST.videos,
  campaigns: [
    {
      id: "camp-home-cleaning",
      name: "Ev Temizliği",
      oldPrice: "",
      newPrice: "Özel Teklif",
      description: "Ev temizliği hizmetinde profesyonel ekip ve ekipmanla detaylı uygulama.",
    },
    {
      id: "camp-sofa-gift",
      name: "Ev Temizliğine Koltuk Yıkama Hediye",
      oldPrice: "",
      newPrice: "Hediye",
      description: "Seçili ev temizliği hizmetlerinde koltuk yıkama hediyesi.",
    },
    {
      id: "camp-post-construction",
      name: "İnşaat Sonrası Temizlik",
      oldPrice: "",
      newPrice: "Teklif Al",
      description: "İnşaat sonrası detaylı temizlik için alanınıza özel fiyatlandırma.",
    },
  ],
  contact: { whatsapp: "", instagram: "", website: "" },
  selectedCtas: CLEANING_CFG?.defaultCtas ?? [],
  years: 8,
  strengths: ["Profesyonel Ekip", "Profesyonel Ekipman", "Buharlı Temizlik"],
  trustNote:
    "Profesyonel ekip, güçlü ekipman ve özenli çalışma anlayışıyla yaşam alanlarında detaylı, güvenli ve etkili temizlik hizmeti sunuyoruz.",
  enabled: true,
};

/* ---------------- Lavia Davet & Organizasyon (kurgu showcase preset) ----------------
   Üçüncü ready example. Tamamen KURGU demo markadır; gerçek müşteri/firma
   değildir. Medya path'leri LAVIA_EVENT_MANIFEST'ten, CTA'lar SECTOR_CONFIGS
   event defaults'tan (reuse) gelir. Contact bilgisi bilerek boştur.
   `enabled` manifest flag'ine bağlı DEĞİLDİR; final export'ta `true`'dur.
   Download sonrası kullanıcı yalnız binary dosyaları kopyalar, kod değiştirmez. */

const EVENT_CFG = SECTOR_CONFIGS.find((c) => c.id === "event");

const LAVIA_EVENT_PRESET: ReadyExamplePreset = {
  id: "lavia-event-v1",
  sectorId: "event",
  title: "Davet & Organizasyon Örneği",
  businessName: "LAVIA DAVET & ORGANİZASYON",
  logo: LAVIA_EVENT_MANIFEST.logo,
  serviceCategories: [
    { id: "wedding", name: "Düğün", images: LAVIA_EVENT_MANIFEST.wedding },
    { id: "engagement", name: "Nişan", images: LAVIA_EVENT_MANIFEST.engagement },
    { id: "birthday", name: "Doğum Günü", images: LAVIA_EVENT_MANIFEST.birthday },
  ],
  videos: LAVIA_EVENT_MANIFEST.videos,
  campaigns: [
    {
      id: "camp-wedding",
      name: "Düğün Organizasyon Paketi",
      oldPrice: "",
      newPrice: "Özel Teklif",
      description: "Salon, masa düzeni ve organizasyon detayları için size özel paket.",
    },
    {
      id: "camp-engagement",
      name: "Nişan & Söz Paketi",
      oldPrice: "",
      newPrice: "Paket Fırsatı",
      description: "Konsept süsleme ve organizasyon detaylarıyla özel gün paketi.",
    },
    {
      id: "camp-birthday",
      name: "Doğum Günü Organizasyonu",
      oldPrice: "",
      newPrice: "Teklif Al",
      description: "Kişiye özel konsept ve organizasyon seçenekleri.",
    },
  ],
  contact: { whatsapp: "", instagram: "", website: "" },
  selectedCtas: EVENT_CFG?.defaultCtas ?? [],
  years: 10,
  strengths: ["Premium Hizmet", "Tecrübe", "Yüksek Müşteri Memnuniyeti"],
  trustNote:
    "Özel günlerinizi zarif detaylar, profesyonel organizasyon ve özenli hizmet anlayışıyla unutulmaz anlara dönüştürüyoruz.",
  enabled: true,
};

/* ---------------- Pusula Akademi (kurgu showcase preset) ----------------
   Dördüncü ready example. Tamamen KURGU demo markadır; gerçek müşteri/firma
   değildir. Medya path'leri PUSULA_AKADEMI_MANIFEST'ten, CTA'lar SECTOR_CONFIGS
   education defaults'tan (reuse) gelir. Contact bilgisi bilerek boştur.
   `enabled` manifest flag'ine bağlı DEĞİLDİR; final export'ta `true`'dur.
   Download sonrası kullanıcı yalnız binary dosyaları kopyalar, kod değiştirmez. */

const EDUCATION_CFG = SECTOR_CONFIGS.find((c) => c.id === "education");

const PUSULA_AKADEMI_PRESET: ReadyExamplePreset = {
  id: "pusula-akademi-v1",
  sectorId: "education",
  title: "Eğitim / Kurs Örneği",
  businessName: "PUSULA AKADEMİ",
  logo: PUSULA_AKADEMI_MANIFEST.logo,
  serviceCategories: [
    { id: "kpss", name: "KPSS Hazırlık", images: PUSULA_AKADEMI_MANIFEST.kpss },
    { id: "english", name: "İngilizce Eğitimi", images: PUSULA_AKADEMI_MANIFEST.english },
    { id: "math", name: "Matematik Özel Ders", images: PUSULA_AKADEMI_MANIFEST.math },
  ],
  videos: PUSULA_AKADEMI_MANIFEST.videos,
  campaigns: [
    {
      id: "camp-kpss",
      name: "KPSS Yoğunlaştırma Programı",
      oldPrice: "",
      newPrice: "Kayıt Fırsatı",
      description: "Planlı konu anlatımı, deneme ve soru çözüm programı.",
    },
    {
      id: "camp-english",
      name: "İngilizce Konuşma Programı",
      oldPrice: "",
      newPrice: "Programı İncele",
      description: "Konuşma pratiği ve seviyeye uygun eğitim programı.",
    },
    {
      id: "camp-math",
      name: "Birebir Matematik",
      oldPrice: "",
      newPrice: "Bilgi Al",
      description: "Öğrencinin seviyesine ve hedefine özel birebir ders planı.",
    },
  ],
  contact: { whatsapp: "", instagram: "", website: "" },
  selectedCtas: EDUCATION_CFG?.defaultCtas ?? [],
  years: 12,
  strengths: ["Tecrübe", "Uzman Personel", "Yüksek Müşteri Memnuniyeti"],
  trustNote:
    "Alanında uzman eğitmenler, planlı programlar ve öğrenci odaklı eğitim yaklaşımıyla hedeflerinize sistemli şekilde hazırlanmanızı sağlıyoruz.",
  enabled: true,
};

/* ---------------- Vista Prime Gayrimenkul (kurgu showcase preset) ----------------
   Beşinci ready example. Tamamen KURGU demo markadır; gerçek müşteri/firma
   değildir. Medya path'leri VISTA_PRIME_MANIFEST'ten, CTA'lar SECTOR_CONFIGS
   realestate defaults'tan (reuse) gelir. Contact bilgisi bilerek boştur.
   `enabled` manifest flag'ine bağlı DEĞİLDİR; final export'ta `true`'dur.
   Download sonrası kullanıcı yalnız binary dosyaları kopyalar, kod değiştirmez. */

const REALESTATE_CFG = SECTOR_CONFIGS.find((c) => c.id === "realestate");

const VISTA_PRIME_PRESET: ReadyExamplePreset = {
  id: "vista-prime-v1",
  sectorId: "realestate",
  title: "Emlak Örneği",
  businessName: "VISTA PRIME GAYRİMENKUL",
  logo: VISTA_PRIME_MANIFEST.logo,
  serviceCategories: [
    { id: "apartments", name: "Satılık Daire", images: VISTA_PRIME_MANIFEST.apartments },
    { id: "villas", name: "Satılık Villa", images: VISTA_PRIME_MANIFEST.villas },
    { id: "investment", name: "Yatırımlık Gayrimenkul", images: VISTA_PRIME_MANIFEST.investment },
  ],
  videos: VISTA_PRIME_MANIFEST.videos,
  campaigns: [
    {
      id: "camp-project",
      name: "Yeni Proje Portföyleri",
      oldPrice: "",
      newPrice: "Portföyü İncele",
      description: "Yeni projelerde seçili daire ve yatırım seçenekleri.",
    },
    {
      id: "camp-villa",
      name: "Lüks Villa Seçkisi",
      oldPrice: "",
      newPrice: "Detay Al",
      description: "İstanbul'da seçkin villa portföylerini inceleyin.",
    },
    {
      id: "camp-investment",
      name: "Yatırım Fırsatları",
      oldPrice: "",
      newPrice: "Danışmana Sor",
      description: "Yatırım hedefinize uygun portföy seçenekleri.",
    },
  ],
  contact: { whatsapp: "", instagram: "", website: "" },
  selectedCtas: REALESTATE_CFG?.defaultCtas ?? [],
  years: 11,
  strengths: ["Tecrübe", "Premium Hizmet", "Yüksek Müşteri Memnuniyeti"],
  trustNote:
    "Seçkin portföyler, doğru yatırım analizi ve profesyonel danışmanlık yaklaşımıyla gayrimenkul sürecinizde yanınızdayız.",
  enabled: true,
};

/* ---------------- Mira Mutfak & Kafe (kurgu showcase preset) ----------------
   Altıncı ready example. Tamamen KURGU demo markadır; gerçek müşteri/firma
   değildir. Medya path'leri MIRA_MUTFAK_MANIFEST'ten, CTA'lar SECTOR_CONFIGS
   food defaults'tan (reuse) gelir. Contact bilgisi bilerek boştur.
   `enabled` manifest flag'ine bağlı DEĞİLDİR; final export'ta `true`'dur.
   Download sonrası kullanıcı yalnız binary dosyaları kopyalar, kod değiştirmez. */

const FOOD_CFG = SECTOR_CONFIGS.find((c) => c.id === "food");

const MIRA_MUTFAK_PRESET: ReadyExamplePreset = {
  id: "mira-mutfak-v1",
  sectorId: "food",
  title: "Yeme & İçme Örneği",
  businessName: "MİRA MUTFAK & KAFE",
  logo: MIRA_MUTFAK_MANIFEST.logo,
  serviceCategories: [
    { id: "home-food", name: "Ev Yemekleri", images: MIRA_MUTFAK_MANIFEST.homeFood },
    { id: "breakfast", name: "Kahvaltı", images: MIRA_MUTFAK_MANIFEST.breakfast },
    { id: "dessert", name: "Tatlı / Pastane", images: MIRA_MUTFAK_MANIFEST.dessert },
  ],
  videos: MIRA_MUTFAK_MANIFEST.videos,
  campaigns: [
    {
      id: "camp-daily-menu",
      name: "Günlük Menü",
      oldPrice: "",
      newPrice: "Menüyü İncele",
      description: "Her gün taze hazırlanan seçili ev yemekleri.",
    },
    {
      id: "camp-weekend-breakfast",
      name: "Hafta Sonu Kahvaltısı",
      oldPrice: "",
      newPrice: "Rezervasyon Bilgisi",
      description: "Zengin kahvaltı seçenekleriyle keyifli hafta sonları.",
    },
    {
      id: "camp-dessert-coffee",
      name: "Tatlı & Kahve",
      oldPrice: "",
      newPrice: "Özel Menü",
      description: "Seçili tatlı ve sıcak içecek kombinasyonları.",
    },
  ],
  contact: { whatsapp: "", instagram: "", website: "" },
  selectedCtas: FOOD_CFG?.defaultCtas ?? [],
  years: 7,
  strengths: ["Kaliteli Hizmet", "Merkezi / Kolay Konum", "Yüksek Müşteri Memnuniyeti"],
  trustNote:
    "Taze ürünler, özenli sunum ve sıcak hizmet anlayışıyla günün her anına lezzet katıyoruz.",
  enabled: true,
};

/* ---------------- Arteva Home (kurgu showcase preset) ----------------
   Yedinci ready example. Tamamen KURGU demo markadır; gerçek müşteri/firma
   değildir. Medya path'leri ARTEVA_HOME_MANIFEST'ten, CTA'lar SECTOR_CONFIGS
   furniture defaults'tan (reuse) gelir. Contact bilgisi bilerek boştur.
   `enabled` manifest flag'ine bağlı DEĞİLDİR; final export'ta `true`'dur.
   Download sonrası kullanıcı yalnız binary dosyaları kopyalar, kod değiştirmez. */

const FURNITURE_CFG = SECTOR_CONFIGS.find((c) => c.id === "furniture");

const ARTEVA_HOME_PRESET: ReadyExamplePreset = {
  id: "arteva-home-v1",
  sectorId: "furniture",
  title: "Mobilya / Ev Dekorasyon Örneği",
  businessName: "ARTEVA HOME",
  logo: ARTEVA_HOME_MANIFEST.logo,
  serviceCategories: [
    { id: "wardrobe", name: "Gardırop", images: ARTEVA_HOME_MANIFEST.wardrobe },
    { id: "tv-unit", name: "TV Ünitesi", images: ARTEVA_HOME_MANIFEST.tvUnit },
    { id: "custom-furniture", name: "Özel Ölçü Mobilya", images: ARTEVA_HOME_MANIFEST.customFurniture },
  ],
  videos: ARTEVA_HOME_MANIFEST.videos,
  campaigns: [
    {
      id: "camp-wardrobe",
      name: "Gardırop Modelleri",
      oldPrice: "",
      newPrice: "Özel Teklif",
      description: "Modern ve fonksiyonel gardırop seçenekleri.",
    },
    {
      id: "camp-tv-unit",
      name: "TV Ünitesi",
      oldPrice: "",
      newPrice: "Sezon Fırsatı",
      description: "Salonunuza uygun modern TV ünitesi tasarımları.",
    },
    {
      id: "camp-custom-furniture",
      name: "Özel Ölçü Tasarım",
      oldPrice: "",
      newPrice: "Teklif Al",
      description: "Alanınıza özel ölçü ve tasarım çözümleri.",
    },
  ],
  contact: { whatsapp: "", instagram: "", website: "" },
  selectedCtas: FURNITURE_CFG?.defaultCtas ?? [],
  years: 9,
  strengths: ["Premium Hizmet", "Kaliteli / Gelişmiş Ekipman", "Yüksek Müşteri Memnuniyeti"],
  trustNote:
    "Modern tasarım, kaliteli malzeme ve yaşam alanınıza özel çözümlerle fonksiyonel ve şık mobilyalar üretiyoruz.",
  enabled: true,
};

/* ---------------- Vita Nova Network (kurgu showcase preset) ----------------
   Sekizinci ready example. Tamamen KURGU demo markadır; gerçek müşteri/firma
   değildir. Medya path'leri VITA_NOVA_NETWORK_MANIFEST'ten, CTA'lar SECTOR_CONFIGS
   network defaults'tan (reuse) gelir. Contact bilgisi bilerek boştur.
   `enabled` manifest flag'ine bağlı DEĞİLDİR; final export'ta `true`'dur.
   Download sonrası kullanıcı yalnız binary dosyaları kopyalar, kod değiştirmez. */

const NETWORK_CFG = SECTOR_CONFIGS.find((c) => c.id === "network");

const VITA_NOVA_NETWORK_PRESET: ReadyExamplePreset = {
  id: "vita-nova-network-v1",
  sectorId: "network",
  title: "Network Marketing Örneği",
  businessName: "VITA NOVA NETWORK",
  logo: VITA_NOVA_NETWORK_MANIFEST.logo,
  serviceCategories: [
    { id: "skincare-products", name: "Cilt Bakım Ürünleri", images: VITA_NOVA_NETWORK_MANIFEST.skincareProducts },
    { id: "home-products", name: "Ev Ürünleri", images: VITA_NOVA_NETWORK_MANIFEST.homeProducts },
    { id: "business-model", name: "Online Kazanç Modeli", images: VITA_NOVA_NETWORK_MANIFEST.businessModel },
  ],
  videos: VITA_NOVA_NETWORK_MANIFEST.videos,
  campaigns: [
    {
      id: "camp-starter-pack",
      name: "Başlangıç Ürün Paketi",
      oldPrice: "",
      newPrice: "Paketi İncele",
      description: "Yeni başlayanlar için seçili ürünlerden oluşan demo paket.",
    },
    {
      id: "camp-home-products",
      name: "Ev Yaşam Ürünleri",
      oldPrice: "",
      newPrice: "Ürünleri İncele",
      description: "Günlük yaşam için seçili ürün kategorileri.",
    },
    {
      id: "camp-business-model",
      name: "Online İş Modeli",
      oldPrice: "",
      newPrice: "Detay Al",
      description: "Çalışma modeli ve başlangıç süreci hakkında bilgi alın.",
    },
  ],
  contact: { whatsapp: "", instagram: "", website: "" },
  selectedCtas: NETWORK_CFG?.defaultCtas ?? [],
  years: 6,
  strengths: ["Hızlı Hizmet", "Yüksek Müşteri Memnuniyeti", "Premium Hizmet"],
  trustNote:
    "Ürün deneyimi, dijital çalışma modeli ve kişisel gelişimi bir araya getiren modern bir doğrudan satış topluluğu sunuyoruz.",
  enabled: true,
};

/* ---------------- Mira Style (kurgu showcase preset) ----------------
   Dokuzuncu ready example. Tamamen KURGU demo markadır; gerçek müşteri/firma
   değildir. Medya path'leri MIRA_STYLE_MANIFEST'ten, CTA'lar SECTOR_CONFIGS
   onlinesales defaults'tan (reuse) gelir. Contact bilgisi bilerek boştur.
   `enabled` manifest flag'ine bağlı DEĞİLDİR; final export'ta `true`'dur.
   Download sonrası kullanıcı yalnız binary dosyaları kopyalar, kod değiştirmez. */

const ONLINESALES_CFG = SECTOR_CONFIGS.find((c) => c.id === "onlinesales");

const MIRA_STYLE_PRESET: ReadyExamplePreset = {
  id: "mira-style-v1",
  sectorId: "onlinesales",
  title: "Online Satış Örneği",
  businessName: "MIRA STYLE",
  logo: MIRA_STYLE_MANIFEST.logo,
  serviceCategories: [
    { id: "womens-fashion", name: "Kadın Giyim", images: MIRA_STYLE_MANIFEST.womensFashion },
    { id: "bags-accessories", name: "Çanta / Aksesuar", images: MIRA_STYLE_MANIFEST.bagsAccessories },
    { id: "jewelry", name: "Takı / Bijuteri", images: MIRA_STYLE_MANIFEST.jewelry },
  ],
  videos: MIRA_STYLE_MANIFEST.videos,
  campaigns: [
    {
      id: "camp-womens-fashion",
      name: "Yeni Sezon Kadın Giyim",
      oldPrice: "1.499 TL",
      newPrice: "999 TL",
      description: "Seçili yeni sezon ürünlerinde demo kampanya.",
    },
    {
      id: "camp-bags-accessories",
      name: "Çanta & Aksesuar",
      oldPrice: "",
      newPrice: "Özel Fiyat",
      description: "Seçili aksesuar modellerinde dönem fırsatı.",
    },
    {
      id: "camp-jewelry",
      name: "Takı Koleksiyonu",
      oldPrice: "",
      newPrice: "Koleksiyonu İncele",
      description: "Yeni sezon takı ve bijuteri seçkisi.",
    },
  ],
  contact: { whatsapp: "", instagram: "", website: "" },
  selectedCtas: ONLINESALES_CFG?.defaultCtas ?? [],
  years: 5,
  strengths: ["Hızlı Hizmet", "Premium Hizmet", "Yüksek Müşteri Memnuniyeti"],
  trustNote:
    "Trend ürünleri özenli seçki, güvenli alışveriş ve hızlı gönderim anlayışıyla Türkiye'nin her yerine ulaştırıyoruz.",
  enabled: true,
};

/* ---------------- Anadolu Sepeti (kurgu showcase preset) ----------------
   Onuncu ready example. Tamamen KURGU demo markadır; gerçek müşteri/firma
   değildir. Medya path'leri ANADOLU_SEPETI_MANIFEST'ten, CTA'lar SECTOR_CONFIGS
   localproducts defaults'tan (reuse) gelir. Contact bilgisi bilerek boştur.
   `enabled` manifest flag'ine bağlı DEĞİLDİR; final export'ta `true`'dur.
   Download sonrası kullanıcı yalnız binary dosyaları kopyalar, kod değiştirmez. */

const LOCALPRODUCTS_CFG = SECTOR_CONFIGS.find((c) => c.id === "localproducts");

const ANADOLU_SEPETI_PRESET: ReadyExamplePreset = {
  id: "anadolu-sepeti-v1",
  sectorId: "localproducts",
  title: "Yöresel Ürünler Örneği",
  businessName: "ANADOLU SEPETİ",
  logo: ANADOLU_SEPETI_MANIFEST.logo,
  serviceCategories: [
    { id: "cheese", name: "Peynir Çeşitleri", images: ANADOLU_SEPETI_MANIFEST.cheese },
    { id: "olive-oil", name: "Zeytin / Zeytinyağı", images: ANADOLU_SEPETI_MANIFEST.oliveOil },
    { id: "honey", name: "Bal / Arı Ürünleri", images: ANADOLU_SEPETI_MANIFEST.honey },
  ],
  videos: ANADOLU_SEPETI_MANIFEST.videos,
  campaigns: [
    {
      id: "camp-breakfast-pack",
      name: "Yöresel Kahvaltı Paketi",
      oldPrice: "",
      newPrice: "Paket Fırsatı",
      description: "Seçili peynir, zeytin ve kahvaltılık ürünlerden oluşan paket.",
    },
    {
      id: "camp-olive-oil",
      name: "Zeytinyağı Seçkisi",
      oldPrice: "",
      newPrice: "Ürünleri İncele",
      description: "Seçili yöresel zeytinyağı ürünleri.",
    },
    {
      id: "camp-gift-pack",
      name: "Hediye Lezzet Paketi",
      oldPrice: "",
      newPrice: "Sipariş Bilgisi",
      description: "Özel günler için hazırlanmış yöresel ürün paketi.",
    },
  ],
  contact: { whatsapp: "", instagram: "", website: "" },
  selectedCtas: LOCALPRODUCTS_CFG?.defaultCtas ?? [],
  years: 8,
  strengths: ["Kaliteli Hizmet", "Hızlı Hizmet", "Yüksek Müşteri Memnuniyeti"],
  trustNote:
    "Anadolu'nun seçkin yöresel lezzetlerini özenli paketleme ve güvenli gönderimle sofralarınıza ulaştırıyoruz.",
  enabled: true,
};

/* ---------------- Nexora Consulting (kurgu showcase preset) ----------------
   On birinci ready example. Tamamen KURGU demo markadır; gerçek müşteri/firma
   değildir. Medya path'leri NEXORA_CONSULTING_MANIFEST'ten, CTA'lar SECTOR_CONFIGS
   professional defaults'tan (reuse) gelir. Contact bilgisi bilerek boştur.
   `enabled` manifest flag'ine bağlı DEĞİLDİR; final export'ta `true`'dur.
   Download sonrası kullanıcı yalnız binary dosyaları kopyalar, kod değiştirmez. */

const PROFESSIONAL_CFG = SECTOR_CONFIGS.find((c) => c.id === "professional");

const NEXORA_CONSULTING_PRESET: ReadyExamplePreset = {
  id: "nexora-consulting-v1",
  sectorId: "professional",
  title: "Profesyonel Hizmet Örneği",
  businessName: "NEXORA CONSULTING",
  logo: NEXORA_CONSULTING_MANIFEST.logo,
  serviceCategories: [
    { id: "digital-marketing", name: "Dijital Pazarlama", images: NEXORA_CONSULTING_MANIFEST.digitalMarketing },
    { id: "web-software", name: "Web Tasarım / Yazılım", images: NEXORA_CONSULTING_MANIFEST.webSoftware },
    { id: "strategy", name: "İş Geliştirme / Strateji", images: NEXORA_CONSULTING_MANIFEST.strategy },
  ],
  videos: NEXORA_CONSULTING_MANIFEST.videos,
  campaigns: [
    {
      id: "camp-digital-growth",
      name: "Dijital Büyüme Analizi",
      oldPrice: "",
      newPrice: "Analiz Talep Et",
      description: "Markanızın dijital büyüme alanlarını birlikte değerlendirelim.",
    },
    {
      id: "camp-web-project",
      name: "Web Proje Paketi",
      oldPrice: "",
      newPrice: "Teklif Al",
      description: "Kurumsal web veya özel yazılım ihtiyacınıza uygun teklif.",
    },
    {
      id: "camp-strategy",
      name: "Strateji Görüşmesi",
      oldPrice: "",
      newPrice: "Görüşme Talep Et",
      description: "Büyüme hedefleriniz için kısa bir strateji görüşmesi.",
    },
  ],
  contact: { whatsapp: "", instagram: "", website: "" },
  selectedCtas: PROFESSIONAL_CFG?.defaultCtas ?? [],
  years: 10,
  strengths: ["Uzman Personel", "Tecrübe", "Premium Hizmet"],
  trustNote:
    "Markaların büyüme hedeflerine strateji, dijital çözümler ve ölçülebilir iş geliştirme yaklaşımıyla destek oluyoruz.",
  enabled: true,
};

/* ---------------- Mavi Nokta Hizmet (kurgu showcase preset) ----------------
   On ikinci ready example. Tamamen KURGU demo markadır; gerçek müşteri/firma
   değildir. Medya path'leri MAVI_NOKTA_MANIFEST'ten, CTA'lar SECTOR_CONFIGS
   other defaults'tan (reuse) gelir. Contact bilgisi bilerek boştur.
   `enabled` manifest flag'ine bağlı DEĞİLDİR; final export'ta `true`'dur.
   Download sonrası kullanıcı yalnız binary dosyaları kopyalar, kod değiştirmez. */

const OTHER_CFG = SECTOR_CONFIGS.find((c) => c.id === "other");

const MAVI_NOKTA_PRESET: ReadyExamplePreset = {
  id: "mavi-nokta-v1",
  sectorId: "other",
  title: "Genel Hizmet Örneği",
  businessName: "MAVİ NOKTA HİZMET",
  logo: MAVI_NOKTA_MANIFEST.logo,
  serviceCategories: [
    { id: "on-site", name: "Yerinde Hizmet", images: MAVI_NOKTA_MANIFEST.onSite },
    { id: "appointment-service", name: "Randevulu Hizmet", images: MAVI_NOKTA_MANIFEST.appointmentService },
    { id: "technical-service", name: "Teknik / Uygulamalı Hizmet", images: MAVI_NOKTA_MANIFEST.technicalService },
  ],
  videos: MAVI_NOKTA_MANIFEST.videos,
  campaigns: [
    {
      id: "camp-on-site",
      name: "Yerinde Hizmet",
      oldPrice: "",
      newPrice: "Teklif Al",
      description: "İhtiyacınıza uygun yerinde hizmet seçenekleri.",
    },
    {
      id: "camp-appointment",
      name: "Randevulu Hizmet",
      oldPrice: "",
      newPrice: "Randevu Bilgisi",
      description: "Planlı ve zamanında hizmet için uygun randevu seçenekleri.",
    },
    {
      id: "camp-project",
      name: "Proje Bazlı Çözüm",
      oldPrice: "",
      newPrice: "Detay Al",
      description: "Özel ihtiyaçlar için proje bazlı hizmet planlaması.",
    },
  ],
  contact: { whatsapp: "", instagram: "", website: "" },
  selectedCtas: OTHER_CFG?.defaultCtas ?? [],
  years: 7,
  strengths: ["Hızlı Hizmet", "Uzman Personel", "Yüksek Müşteri Memnuniyeti"],
  trustNote:
    "Bireysel ve kurumsal ihtiyaçlara hızlı, planlı ve güvenilir hizmet çözümleri sunuyoruz.",
  enabled: true,
};

export const READY_EXAMPLES: ReadyExamplePreset[] = SECTOR_CONFIGS.map((cfg) => {
  if (cfg.id === "beauty") {
    return LUNA_BEAUTY_PRESET;
  }

  if (cfg.id === "cleaning") {
    return NOVA_CLEAN_PRESET;
  }

  if (cfg.id === "event") {
    return LAVIA_EVENT_PRESET;
  }

  if (cfg.id === "education") {
    return PUSULA_AKADEMI_PRESET;
  }

  if (cfg.id === "realestate") {
    return VISTA_PRIME_PRESET;
  }

  if (cfg.id === "food") {
    return MIRA_MUTFAK_PRESET;
  }

  if (cfg.id === "furniture") {
    return ARTEVA_HOME_PRESET;
  }

  if (cfg.id === "network") {
    return VITA_NOVA_NETWORK_PRESET;
  }

  if (cfg.id === "onlinesales") {
    return MIRA_STYLE_PRESET;
  }

  if (cfg.id === "localproducts") {
    return ANADOLU_SEPETI_PRESET;
  }

  if (cfg.id === "professional") {
    return NEXORA_CONSULTING_PRESET;
  }

  if (cfg.id === "other") {
    return MAVI_NOKTA_PRESET;
  }

  // Tüm sektörler için ortak, güvenli boş preset. Medya ve iletişim verisi
  // yalnızca kontrollü asset / gerçek değer mevcut olduğunda doldurulur.
  const preset: ReadyExamplePreset = {
    id: `ready-${cfg.id}`,
    sectorId: cfg.id,
    title: READY_EXAMPLE_TITLES[cfg.id] ?? `${cfg.label} Örneği`,
    businessName: "Örnek İşletme",
    serviceCategories: [],
    videos: [],
    campaigns: [],
    contact: { whatsapp: "", instagram: "", website: "" },
    selectedCtas: [],
    enabled: false,
  };

  return preset;
});

export function getReadyExamplePreset(id: string): ReadyExamplePreset | undefined {
  return READY_EXAMPLES.find((p) => p.id === id);
}

/* Preset → DemoFormData. Sektör label'ı SECTOR_CONFIGS'ten, default CTA'lar
   sektörün kendi defaultCtas'ından gelir. Böylece hazır örnek de mevcut motoru kullanır. */
export function readyPresetToFormData(preset: ReadyExamplePreset): DemoFormData {
  const cfg = SECTOR_CONFIGS.find((c) => c.id === preset.sectorId);
  return {
    ...defaultDemoFormData,
    firmaAdi: preset.businessName,
    sektor: cfg?.label ?? preset.businessName,
    serviceCategories: preset.serviceCategories,
    videolar: preset.videos,
    campaigns: preset.campaigns,
    selectedCtas: preset.selectedCtas.length > 0 ? preset.selectedCtas : (cfg?.defaultCtas ?? []),
    whatsapp: preset.contact.whatsapp,
    instagram: preset.contact.instagram,
    website: preset.contact.website,
    anaIletisimKanali: "whatsapp",
    tecrubeYili: preset.years ?? null,
    gucluYonler: preset.strengths ?? [],
    guvenNotu: preset.trustNote ?? "",
  };
}

/* ---------------- Canlı Referans (Live Reference) ----------------
   Tek global config. Bu fazda gerçek veri girilmez; enabled=false'tur.
   Gerçek güzellik merkezi Instagram hesabı sonraki fazda bağlanacak. */
export interface LiveReferenceConfig {
  enabled: boolean;
  businessName: string;
  sectorId: string;
  instagramUrl: string;
  triggerInstruction?: string;
  triggerKeyword?: string;
}

export const LIVE_REFERENCE_CONFIG: LiveReferenceConfig = {
  enabled: false,
  businessName: "",
  sectorId: "beauty",
  instagramUrl: "",
  triggerInstruction: "",
  triggerKeyword: "MERHABA",
};